import { Box } from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import usePreviousEffect from "@pathwright/ui/src/components/hooks/usePreviousEffect"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import View from "@pathwright/ui/src/components/ui/View"
import { PreloadableAdaptedSignedInContainer } from "@pathwright/web/src/modules/auth/LazyAdaptedSignedInContainer"
import { OrderSubscriptionFlow } from "@pathwright/web/src/modules/order/OrderSubscriptionCard"
import { RegistrationOrderConfirmation } from "@pathwright/web/src/modules/order/RegistrationOrderConfirmationCard"
import { getVisibilityOptionFromParams } from "account/subscription/utils"
import { ConnectStores } from "lib/core/store"
import ResourceOffering from "library/show/components/about/offerings/ResourceOffering"
import ResourceOfferings from "library/show/components/about/offerings/ResourceOfferings"
import { loadResource } from "path/middleware"
import { useEffect } from "react"
import {
  REGISTER_STATE_AUTH,
  REGISTER_STATE_OFFERING_LIST,
  REGISTER_STATE_OFFERING_OPTIONS,
  REGISTER_STATE_PAYMENT,
  REGISTER_STATE_REGISTERABLE,
  REGISTER_STATE_REGISTERED,
  REGISTER_STATE_REGISTRATION_CLOSED,
  REGISTER_STATE_SUBSCRIBE,
  REGISTER_STATE_UNUSABLE_SUBSCRIPTION
} from "../constants"
import Register from "./Register"
import RegisterAuthGate from "./RegisterAuthGate"
import "./RegisterFlow.css"
import RegisterFlowHeader from "./RegisterFlowHeader"
import Registered from "./Registered"
import RegistrationClosed from "./RegistrationClosed"
import UnusableSubscription from "./UnusableSubscription"

const RegisterFlow = (props) => {
  const {
    resource,
    offering,
    state,
    registration,
    location,
    match,
    alreadyRegistered,
    clearRegisterErrors,
    registering_outside_of_flow,
    flowError,
    isFull,
    staffContact,
    invitation,
    inviteToken,
    onClose,
    onOrderFulfilled
  } = props

  const setOffering = () => {
    if (registering_outside_of_flow) return

    loadResource(props).then(() => {
      const { cohortId } = match.params
      const { ic: inviteToken, ro: offeringOption } = location.query
      const visibilityCodeObj = getVisibilityOptionFromParams()
      if (inviteToken) {
        // Prevent the modal from closing if this is an invitation
        // TODO: allow user to close modal?
        window.App.getStore("navigation").action.preventModalClose()
      }
      window.App.getStore("register").action.setOffering(
        cohortId ? parseInt(cohortId) : null,
        offeringOption ? parseInt(offeringOption) : null,
        inviteToken,
        visibilityCodeObj
      )
    })
  }

  useEffect(setOffering, [])

  // Improve UX slightly by preloading signed in component.
  useEffect(() => {
    PreloadableAdaptedSignedInContainer.preload()
  }, [])

  usePreviousEffect(
    ([prevLocation, prevMatch, prevState]) => {
      if (
        prevLocation.query.ro !== location.query.ro ||
        prevMatch.params.cohortId !== match.params.cohortId ||
        // Store likely reset after sign-out.
        (prevState && !state)
      ) {
        setOffering()
      }
    },
    [location, match, state]
  )

  return (
    <div className="RegisterFlow">
      {!!resource && (
        <RegisterFlowHeader
          offering={offering}
          resource={resource}
          invitation={invitation}
          onClose={onClose}
        />
      )}

      {flowError && <Alert error={flowError} onClear={clearRegisterErrors} />}

      {match.params.orderId ? (
        <Box minHeight="300px">
          <RegistrationOrderConfirmation
            orderId={parseInt(match.params.orderId)}
            cohortId={parseInt(match.params.cohortId)}
            onOrderFulfilled={onOrderFulfilled}
          />
        </Box>
      ) : (
        (() => {
          switch (state) {
            case REGISTER_STATE_AUTH || REGISTER_STATE_REGISTERABLE:
              return <RegisterAuthGate inviteToken={inviteToken} />
            case REGISTER_STATE_OFFERING_LIST:
              return (
                <View paddingTop>
                  <ResourceOfferings />
                </View>
              )
            case REGISTER_STATE_OFFERING_OPTIONS:
              return (
                <ResourceOffering offering={offering} resource={resource} />
              )
            case REGISTER_STATE_REGISTRATION_CLOSED:
              return (
                <RegistrationClosed
                  offering={offering}
                  invitation={invitation}
                  isFull={isFull}
                  staffContact={staffContact}
                />
              )
            case REGISTER_STATE_UNUSABLE_SUBSCRIPTION:
              return <UnusableSubscription />
            case REGISTER_STATE_SUBSCRIBE:
              return (
                <OrderSubscriptionFlow
                  planIds={offering.include_in_subscription_plans}
                  productIds={offering.include_with_products}
                  redirectTo={window.location.pathname + window.location.search}
                />
              )
            case REGISTER_STATE_PAYMENT:
              return <Register />
            case REGISTER_STATE_REGISTERED:
              return (
                <Registered
                  registration={registration}
                  alreadyRegistered={alreadyRegistered}
                />
              )
            default:
              return (
                <div
                  style={{
                    minHeight: "300px"
                  }}
                >
                  <LoadingCircle brand />
                </div>
              )
          }
        })()
      )}
    </div>
  )
}

RegisterFlow.displayName = "RegisterFlow"

const mapStateToProps = function (state, ownProps) {
  const {
    resource,
    offering,
    registration,
    already_registered,
    state: register_state,
    invitation,
    invitation_error,
    registration_error,
    registering_outside_of_flow
  } = state.registerStore.state
  const { is_authenticated } = state.authStore.state
  const { ic: inviteToken } = ownProps.location.query

  const { clearRegisterErrors, register } = state.registerStore.action

  // TODO: better error handling, better flow, better everything...
  const flowError = registration_error || invitation_error

  const staffContact = state.registerStore.request.offeringStaffContact?.()

  const seatsAvailable = state.resourceStore.request.seatsAvailable(
    offering,
    invitation
  )
  const isFull = !(seatsAvailable === -1 || seatsAvailable > 0)

  const onClose = state.registerStore.request.onCloseRegisterFlow
  const alreadyRegistered = already_registered

  const props = _.assign(
    {
      register,
      // state props
      resource,
      offering,
      registration,
      alreadyRegistered,
      state: register_state,
      staffContact,
      invitation,
      isFull,
      clearRegisterErrors,
      onClose,
      is_authenticated,
      registration_error,
      flowError,
      registering_outside_of_flow,
      inviteToken
    },
    ownProps
  )

  return props
}

const watch = [
  "invitation",
  "offering",
  // "resource",
  "state",
  "display_price",
  "registration",
  "already_registered",
  "invitation_error",
  "registration_error",
  "registering_outside_of_flow"
]

const stores = [
  { store: "register", watch },
  { store: "resource", watch: false },
  { store: "navigation", watch: false },
  { store: "auth", watch: ["is_authenticated"] }
]

export default ConnectStores(RegisterFlow, stores, {}, mapStateToProps)
